import { useCallback, useEffect, useMemo, useRef, type ReactElement } from "react";
import { ConfirmDialog } from "@primitives/index";
import { PresenceStack, WorkflowBoard } from "@compositions/index";
import type { DestinationQueue, RailSelection } from "@compositions/index";
import { useChildWorkflows, useRecordWorkItemView, useTaskTypes, useWorkflow } from "../hooks";
import { toTaskTypeOptions } from "../taskTypes";
import type { WorkItem, Workflow } from "../types";
import { OUT_OF_QUEUE_COMPLETION_MESSAGE } from "../taskCompletionConfirmation";
import type { QueueTreeResponse } from "../../queues";
import { useWorkflowRealtime } from "../../realtime";
import { useRecordPresence } from "../../realtime/presence";
import { useGalileoNavigation, resolveWorkItemActionTarget } from "../../navigation";
import type { WorkItemActionTarget } from "../../navigation";
import { useBoardMutations } from "./useBoardMutations";
import { useBoardSelection } from "./useBoardSelection";

export interface BoardBodyProps {
  readonly id: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly currentUserId: string | undefined;
  /** Current queue context; becomes a spawned thread's originating queue. */
  readonly originatingQueueId: string | undefined;
  readonly queueTree: QueueTreeResponse | undefined;
  /** Operational queues returned by /queues/mine for the current user. */
  readonly memberQueueIds: ReadonlySet<string>;
  /** Queues the caller may file a task onto from the rail composer. */
  readonly destinationQueues: readonly DestinationQueue[];
  /** The scene owns the compact lane flag so its header can react to it. */
  readonly laneOpen: boolean;
  readonly onLaneOpenChange: (open: boolean) => void;
}

/**
 * Board view body. Owns the rail/thread selection (URL-driven) and the
 * on-demand thread data; the composition stays presentational. The scene
 * owns the header, the workflow-level realtime subscription, and the
 * workflow presence roster.
 */
export function BoardBody({
  id,
  entityType,
  entityId,
  currentUserId,
  originatingQueueId,
  queueTree,
  memberQueueIds,
  destinationQueues,
  laneOpen,
  onLaneOpenChange,
}: BoardBodyProps): ReactElement {
  const { selectedItemId, selectedThreadId, composeTask, writeSelection, clearCompose } =
    useBoardSelection();

  const { data: workflow, isPlaceholderData: isWorkflowStale } = useWorkflow(
    id,
    entityType,
    entityId,
  );
  const taskTypesQuery = useTaskTypes();
  const taskTypeOptions = useMemo(
    () => toTaskTypeOptions(taskTypesQuery.data?.taskTypes ?? []),
    [taskTypesQuery.data],
  );

  const { navigateToGalileo, navigateExternal } = useGalileoNavigation();

  // One resolver for the "Go" target under the fixed precedence (explicit
  // externalActionUrl > computed deep link > none). Shared by the click
  // handler and the visibility predicate so they cannot disagree.
  const resolveTaskTarget = useCallback(
    (task: WorkItem): WorkItemActionTarget | null =>
      resolveWorkItemActionTarget({
        taskType: task.taskType ?? "",
        entityType: task.entityType.length > 0 ? task.entityType : entityType,
        entityId: task.entityId.length > 0 ? task.entityId : entityId,
        routingTags: task.routingTags,
        externalActionUrl: task.externalActionUrl,
      }),
    [entityType, entityId],
  );
  const handleNavigateToGalileoTask = useCallback(
    (task: WorkItem) => {
      const target = resolveTaskTarget(task);
      if (target === null) return;
      if (target.kind === "external") navigateExternal(target.url);
      else navigateToGalileo(target.path);
    },
    [resolveTaskTarget, navigateToGalileo, navigateExternal],
  );
  const canNavigateToGalileoTask = useCallback(
    (task: WorkItem): boolean => resolveTaskTarget(task) !== null,
    [resolveTaskTarget],
  );

  // Tasks and notes both host child threads; a message row does not.
  const selectedThreadHost = useMemo<WorkItem | null>(() => {
    if (selectedItemId === null || workflow === undefined) return null;
    const match = workflow.workItems.find(
      (i) => i.id === selectedItemId && (i.type === "task" || i.type === "note"),
    );
    return match ?? null;
  }, [selectedItemId, workflow]);
  const selectedThreadHostId = selectedThreadHost?.id ?? null;

  // Presence is task-scoped, so it keys on the narrower projection.
  const selectedTaskId = useMemo<string | null>(() => {
    if (selectedItemId === null || workflow === undefined) return null;
    const match = workflow.workItems.find((i) => i.id === selectedItemId && i.type === "task");
    return match?.id ?? null;
  }, [selectedItemId, workflow]);
  const taskViewers = useRecordPresence("workItem", selectedTaskId ?? undefined);
  const taskPresenceSlot =
    selectedTaskId !== null && taskViewers.length > 0 ? (
      <PresenceStack members={taskViewers} recordLabel="this task" filterSelfWhenAlone={true} />
    ) : undefined;

  // Durable view tracking: one `workitem.viewed` record per distinct open.
  // The ref guard drops back-to-back duplicates (re-render, StrictMode
  // double effect) but re-logs A -> B -> A.
  const { mutate: recordWorkItemView } = useRecordWorkItemView({
    workflowId: id,
    entityType,
    entityId,
  });
  const lastLoggedViewId = useRef<string | null>(null);
  useEffect(() => {
    if (selectedItemId === null || workflow === undefined) return;
    const item = workflow.workItems.find((i) => i.id === selectedItemId);
    if (item === undefined || (item.type !== "task" && item.type !== "note")) return;
    if (lastLoggedViewId.current === selectedItemId) return;
    lastLoggedViewId.current = selectedItemId;
    recordWorkItemView(selectedItemId);
  }, [selectedItemId, workflow, recordWorkItemView]);

  const childWorkflowsQuery = useChildWorkflows({
    workflowId: id,
    workItemId: selectedThreadHostId ?? "",
    entityType,
    entityId,
    enabled: selectedThreadHostId !== null,
  });
  const threads = useMemo(
    () => childWorkflowsQuery.data?.items ?? [],
    [childWorkflowsQuery.data?.items],
  );
  // Detail panel readiness: header, notes, and threads reveal together, so
  // no loaded content sits above an active skeleton. The thread list is
  // pending only while its first fetch is in flight; a host the detail
  // already reports as thread-less renders its empty state at once, and a
  // placeholder list (previous host, dimmed) is not pending.
  const threadsPending =
    selectedThreadHost !== null &&
    selectedThreadHost.childWorkflowCount > 0 &&
    childWorkflowsQuery.isLoading;

  // Reconcile the thread selection with the list (`replace: true` — an
  // auto-selection is not a history step). Placeholder threads belong to
  // the previous host, so the effect waits for the real list.
  const threadsArePlaceholder = childWorkflowsQuery.isPlaceholderData;
  useEffect(() => {
    if (threadsArePlaceholder) return;
    if (selectedThreadHostId === null || threads.length === 0) {
      if (selectedThreadId !== null) writeSelection({ threadId: null }, { replace: true });
      return;
    }
    if (selectedThreadId === null || !threads.some((t) => t.id === selectedThreadId)) {
      const first = threads[0];
      if (first !== undefined) writeSelection({ threadId: first.id }, { replace: true });
    }
  }, [threadsArePlaceholder, selectedThreadHostId, threads, selectedThreadId, writeSelection]);

  const {
    data: activeThread,
    isLoading: isLoadingActiveThread,
    isPlaceholderData: isActiveThreadStale,
  } = useWorkflow(selectedThreadId ?? "", entityType, entityId);
  useWorkflowRealtime({ workflowId: selectedThreadId ?? "", entityType, entityId });

  const { handlers, busy, pendingCompletionId } = useBoardMutations({
    id,
    entityType,
    entityId,
    workflow,
    activeThread,
    selectedThreadId,
    selectedThreadHostId,
    originatingQueueId,
    memberQueueIds,
    writeSelection,
  });

  const handleSelectItem = useCallback(
    // A rail selection is a navigation step (push). Thread focus resets so
    // the new task's first thread auto-selects.
    (next: RailSelection) => writeSelection({ itemId: next, threadId: null }),
    [writeSelection],
  );
  const handleSelectThread = useCallback(
    (next: string | null) => writeSelection({ threadId: next }),
    [writeSelection],
  );

  const emptyWorkflow: Workflow = {
    id,
    entityType,
    entityId,
    entityDisplayName: "",
    kind: "",
    title: "",
    createdAt: new Date().toISOString(),
    createdBy: null,
    createdByDisplayName: null,
    parentWorkflowId: null,
    parentWorkItemId: null,
    workItemTally: null,
    workItems: [],
  };

  return (
    <>
      <ConfirmDialog
        open={pendingCompletionId !== null}
        title="Complete task?"
        message={OUT_OF_QUEUE_COMPLETION_MESSAGE}
        confirmLabel="Complete"
        onConfirm={handlers.confirmCompletion}
        onCancel={handlers.cancelCompletion}
        testId="out-of-queue-completion-confirm"
      />
      <WorkflowBoard
        // Keyed by the workflow on screen, not the URL id: the previous
        // workflow shows dimmed while the next loads; when it lands, local
        // state (drafts, open lanes, creator) starts clean.
        key={workflow?.id ?? id}
        workflow={workflow ?? emptyWorkflow}
        isLoading={workflow === undefined || threadsPending}
        isStale={isWorkflowStale}
        approverQueueIds={memberQueueIds}
        taskTypeOptions={taskTypeOptions}
        destinationQueues={destinationQueues}
        requireExplicitDestination={workflow?.kind === "follow-up"}
        initialCreatorOpen={composeTask}
        onCreatorClosed={clearCompose}
        selectedItemId={selectedItemId}
        onSelectItem={handleSelectItem}
        laneOpen={laneOpen}
        onLaneOpenChange={onLaneOpenChange}
        threads={threads}
        isLoadingThreads={threadsPending}
        isThreadsStale={threadsArePlaceholder}
        selectedThreadId={selectedThreadId}
        onSelectThread={handleSelectThread}
        isLoadingActiveThread={isLoadingActiveThread}
        isActiveThreadStale={isActiveThreadStale}
        onSendGeneralMessage={handlers.sendGeneralMessage}
        onSendThreadMessage={handlers.sendThreadMessage}
        onStartThread={handlers.startThread}
        onAddTask={handlers.addTask}
        onAddNote={handlers.addNote}
        onCompleteWorkItem={handlers.completeWorkItem}
        onApproveWorkItem={handlers.approveWorkItem}
        onRejectWorkItem={handlers.rejectWorkItem}
        onSnoozeWorkItem={handlers.snoozeWorkItem}
        onReassignWorkItemQueue={handlers.reassignWorkItemQueue}
        onPinNote={handlers.pinNote}
        onEditNote={handlers.editNote}
        onApproveThreadAnswer={handlers.approveThreadAnswer}
        onRejectThreadAnswer={handlers.rejectThreadAnswer}
        onCloseConversation={handlers.closeConversation}
        onCloseThread={handlers.closeThread}
        onNavigateToGalileoTask={handleNavigateToGalileoTask}
        canNavigateToGalileoTask={canNavigateToGalileoTask}
        {...busy}
        {...(currentUserId !== undefined ? { currentUserId } : {})}
        {...(originatingQueueId !== undefined ? { originatingQueueId } : {})}
        {...(queueTree !== undefined ? { queueTree } : {})}
        {...(activeThread !== undefined ? { activeThread } : {})}
        {...(taskPresenceSlot !== undefined ? { taskPresenceSlot } : {})}
      />
    </>
  );
}
